// src/services/ConflictResolutionService.js
// Atomic booking conflict prevention with Firebase transactions
import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    query,
    runTransaction,
    serverTimestamp,
    where
} from 'firebase/firestore';
import { ErrorTrackingService } from './ErrorTrackingService';
import { FirebaseService } from './FirebaseService';
import { GoogleCalendarAPIService } from './GoogleCalendarAPIService';
import { NotificationService } from './NotificationService';

export class ConflictResolutionService {
  
  // Configuration constants
  static LOCK_TIMEOUT = parseInt(process.env.REACT_APP_BOOKING_LOCK_TIMEOUT) || 30000; // 30 seconds
  static MAX_RETRY_ATTEMPTS = parseInt(process.env.REACT_APP_CONFLICT_RETRY_ATTEMPTS) || 3;
  static CONFLICT_RESOLUTION_ENABLED = process.env.REACT_APP_ENABLE_CONFLICT_RESOLUTION !== 'false';
  static STRICT_TIME_VALIDATION = process.env.REACT_APP_STRICT_TIME_VALIDATION === 'true';

  // ==========================================
  // CORE CONFLICT DETECTION METHODS
  // ==========================================

  /**
   * Check if a time slot is available (atomic operation)
   */
  static async checkTimeSlotAvailability(
    businessId,
    dateTime,
    serviceNameOrDuration = null,
    durationOrOptions = 60,
    maybeOptions = {}
  ) {
    let serviceName = null;
    let duration = 60;
    let options = {};

    try {
      const { db } = FirebaseService.getInstances();

      if (typeof serviceNameOrDuration === 'string' || serviceNameOrDuration === null) {
        serviceName = serviceNameOrDuration;

        if (typeof durationOrOptions === 'number') {
          duration = durationOrOptions;
          options = typeof maybeOptions === 'object' && maybeOptions ? { ...maybeOptions } : {};
        } else if (typeof durationOrOptions === 'object' && durationOrOptions) {
          options = { ...durationOrOptions };
        }
      } else if (typeof serviceNameOrDuration === 'number') {
        duration = serviceNameOrDuration;

        if (typeof durationOrOptions === 'object' && durationOrOptions) {
          options = { ...durationOrOptions };
        } else if (typeof durationOrOptions === 'string') {
          options = { ...options, excludeBookingId: durationOrOptions };
        }
      } else if (typeof serviceNameOrDuration === 'object' && serviceNameOrDuration) {
        options = { ...serviceNameOrDuration };
      }

      if (typeof maybeOptions === 'object' && maybeOptions) {
        options = { ...options, ...maybeOptions };
      }

      const excludeBookingId = options.excludeBookingId || null;

      ErrorTrackingService.addBreadcrumb(
        'Time slot availability check started',
        'conflict_resolution',
        'info',
        { businessId, dateTime, duration, serviceName, excludeBookingId }
      );

      // Parse the booking time
      const bookingStart = new Date(dateTime);
      if (isNaN(bookingStart.getTime())) {
        throw new Error('Invalid booking start time provided for availability check');
      }

      const bookingEnd = new Date(bookingStart.getTime() + (duration * 60000));

      const businessRef = doc(db, 'businesses', businessId);
      const businessSnap = await getDoc(businessRef);
      const businessData = businessSnap.exists() ? businessSnap.data() : {};
      const businessPhone = (
        businessData?.businessPhone ||
        businessData?.whatsapp_number ||
        businessData?.phone_number ||
        businessData?.phoneNumber ||
        businessId
      )?.toString().replace(/whatsapp:/gi, '').replace(/[^0-9+]/g, '');

      const availabilityDate = [
        bookingStart.getFullYear(),
        String(bookingStart.getMonth() + 1).padStart(2, '0'),
        String(bookingStart.getDate()).padStart(2, '0')
      ].join('-');
      const availabilityTime = [
        String(bookingStart.getHours()).padStart(2, '0'),
        String(bookingStart.getMinutes()).padStart(2, '0')
      ].join(':');

      const unavailableResult = (errorMessage = null) => ({
        isAvailable: false,
        available: false,
        conflicts: [],
        requestedSlot: {
          start: bookingStart.toISOString(),
          end: bookingEnd.toISOString(),
          duration
        },
        checkedAt: new Date().toISOString(),
        error: errorMessage,
        capacity: {
          overlappingCount: null,
          overlappingServiceCount: null,
          maxConcurrent: null,
          serviceLimit: null,
          bufferMinutes: null,
          remainingOverall: null,
          remainingService: null
        }
      });

      if (!businessPhone) {
        return unavailableResult('Business phone is required for availability check');
      }

      const response = await fetch(`${GoogleCalendarAPIService.getAvailabilityURL()}/api/availability`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          business_phone: businessPhone,
          service_type: serviceName || 'appointment',
          date: availabilityDate,
          time: availabilityTime
        })
      });

      if (!response.ok) {
        return unavailableResult(`Availability check failed: ${response.status}`);
      }

      const result = await response.json();
      const isAvailable = result.available === true;
      const conflicts = Array.isArray(result.conflicts) ? result.conflicts : [];

      ErrorTrackingService.addBreadcrumb(
        'Time slot availability check completed',
        'conflict_resolution',
        isAvailable ? 'info' : 'warning',
        {
          businessId,
          isAvailable,
          conflictCount: conflicts.length,
          requestedTime: dateTime
        }
      );

      return {
        isAvailable,
        available: isAvailable,
        conflicts,
        requestedSlot: {
          start: result.slot_start || bookingStart.toISOString(),
          end: result.slot_end || bookingEnd.toISOString(),
          duration
        },
        checkedAt: new Date().toISOString(),
        message: result.message,
        capacity: {
          overlappingCount: null,
          overlappingServiceCount: null,
          maxConcurrent: null,
          serviceLimit: null,
          bufferMinutes: null,
          remainingOverall: null,
          remainingService: null
        }
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'checkTimeSlotAvailability',
        businessId,
        dateTime,
        duration: typeof durationOrOptions === 'number' ? durationOrOptions : duration,
        serviceName: typeof serviceNameOrDuration === 'string' ? serviceNameOrDuration : undefined
      });

      return {
        isAvailable: false,
        available: false,
        conflicts: [],
        requestedSlot: dateTime ? {
          start: dateTime,
          end: null,
          duration: typeof durationOrOptions === 'number' ? durationOrOptions : duration
        } : null,
        checkedAt: new Date().toISOString(),
        error: error.message
      };
    }
  }

  /**
   * Acquire time slot lock for atomic booking creation
   */
  static async acquireTimeSlotLock(businessId, dateTime, duration = 60) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const lockId = `lock_${businessId}_${dateTime}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const lockData = {
        lockId,
        businessId,
        dateTime,
        duration,
        acquiredAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + this.LOCK_TIMEOUT).toISOString(),
        status: 'active'
      };
      
      // Create lock document
      const lockRef = await addDoc(
        collection(db, 'booking_locks'),
        lockData
      );
      
      ErrorTrackingService.addBreadcrumb(
        'Time slot lock acquired',
        'conflict_resolution',
        'info',
        { businessId, lockId, dateTime }
      );
      
      return {
        lockId,
        lockRef: lockRef.id,
        expiresAt: lockData.expiresAt,
        acquiredAt: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'acquireTimeSlotLock',
        businessId,
        dateTime
      });
      
      throw error;
    }
  }

  /**
   * Release time slot lock
   */
  static async releaseLock(lockInfo) {
    try {
      const { db } = FirebaseService.getInstances();
      
      if (lockInfo.lockRef) {
        const lockRef = doc(db, 'booking_locks', lockInfo.lockRef);
        await runTransaction(db, async (transaction) => {
          transaction.update(lockRef, {
            status: 'released',
            releasedAt: serverTimestamp()
          });
        });
        
        ErrorTrackingService.addBreadcrumb(
          'Time slot lock released',
          'conflict_resolution',
          'info',
          { lockId: lockInfo.lockId }
        );
      }
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'releaseLock',
        lockId: lockInfo.lockId
      });
      
      // Don't throw - lock will expire automatically
    }
  }

  // ==========================================
  // ATOMIC BOOKING OPERATIONS
  // ==========================================

  /**
   * Create booking with atomic conflict resolution
   */
  static async createBookingWithConflictResolution(businessId, bookingData, options = {}) {
    if (!this.CONFLICT_RESOLUTION_ENABLED) {
      // Fallback to original method if disabled
      const { BookingService } = await import('./BookingService');
      return await BookingService.createBooking(businessId, bookingData);
    }

    let attempt = 0;
    let lastError = null;
    
    while (attempt < this.MAX_RETRY_ATTEMPTS) {
      attempt++;
      
      try {
        ErrorTrackingService.addBreadcrumb(
          'Atomic booking creation attempt',
          'conflict_resolution',
          'info',
          { businessId, attempt, maxAttempts: this.MAX_RETRY_ATTEMPTS }
        );
        
        // Step 1: Validate booking data
        const validation = await this.validateBookingRequest(businessId, bookingData);
        if (!validation.isValid) {
          throw new Error(`Booking validation failed: ${validation.errors.join(', ')}`);
        }
        
        // Step 2: Check initial availability
        const bookingDateTime = new Date(`${bookingData.bookingDate}T${bookingData.time}`);
        const availability = await this.checkTimeSlotAvailability(
          businessId,
          bookingDateTime.toISOString(),
          bookingData.serviceName || bookingData.service_type || null,
          bookingData.duration || 60
        );
        
        if (!availability.isAvailable) {
          return {
            success: false,
            error: 'Time slot not available',
            conflicts: availability.conflicts,
            suggestedAlternatives: await this.suggestAlternativeTimeSlots(
              businessId,
              bookingData.bookingDate,
              bookingData.duration || 60
            )
          };
        }
        
        // Step 3: Acquire lock and perform atomic creation
        const result = await this.performAtomicBookingCreation(businessId, bookingData);
        
        if (result.success) {
          ErrorTrackingService.addBreadcrumb(
            'Atomic booking creation successful',
            'conflict_resolution',
            'info',
            { businessId, bookingId: result.bookingId, attempt }
          );
          
          return result;
        } else {
          lastError = new Error(result.error || 'Atomic booking creation failed');
        }
        
      } catch (error) {
        lastError = error;
        
        if (error.message.includes('Time slot not available') || error.message.includes('validation failed')) {
          // Don't retry for these types of errors
          break;
        }
        
        ErrorTrackingService.addBreadcrumb(
          'Atomic booking attempt failed',
          'conflict_resolution',
          'warning',
          { businessId, attempt, error: error.message }
        );
        
        // Wait before retry (exponential backoff)
        if (attempt < this.MAX_RETRY_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    // All attempts failed
    ErrorTrackingService.captureError(lastError, {
      service: 'ConflictResolutionService',
      method: 'createBookingWithConflictResolution',
      businessId,
      attempts: this.MAX_RETRY_ATTEMPTS,
      bookingData: {
        serviceName: bookingData.serviceName,
        customerName: bookingData.customerName,
        bookingDate: bookingData.bookingDate,
        time: bookingData.time
      }
    });
    
    return {
      success: false,
      error: `Booking creation failed after ${this.MAX_RETRY_ATTEMPTS} attempts: ${lastError?.message}`,
      attempts: this.MAX_RETRY_ATTEMPTS
    };
  }

  /**
   * Perform atomic booking creation with Firebase transaction
   */
  static async performAtomicBookingCreation(businessId, bookingData) {
    const { db } = FirebaseService.getInstances();
    let lock = null;

    try {
      // Acquire lock
      const bookingDateTime = new Date(`${bookingData.bookingDate}T${bookingData.time}`);
      lock = await this.acquireTimeSlotLock(
        businessId,
        bookingDateTime.toISOString(),
        bookingData.duration || 60
      );
      
      // Perform atomic booking creation within transaction
      const result = await runTransaction(db, async (transaction) => {
        // Double-check availability within transaction
        const availability = await this.checkTimeSlotAvailability(
          businessId,
          bookingDateTime.toISOString(),
          bookingData.serviceName || bookingData.service_type || null,
          bookingData.duration || 60
        );
        
        if (!availability.isAvailable) {
          throw new Error('Time slot became unavailable during transaction');
        }
        
        // Find or create customer
        let customerId = bookingData.customerId;
        
        if (!customerId) {
          const { BookingService } = await import('./BookingService');
          const existingCustomer = await BookingService.findCustomerByPhone(businessId, bookingData.customerPhone);
          
          if (existingCustomer) {
            customerId = existingCustomer.id;
          } else {
            const customerResult = await BookingService.createCustomer(businessId, {
              name: bookingData.customerName,
              phone: bookingData.customerPhone,
              email: bookingData.customerEmail || '',
              whatsapp: bookingData.customerWhatsapp || bookingData.customerPhone
            });
            
            if (!customerResult.success) {
              throw new Error('Failed to create customer: ' + customerResult.error);
            }
            
            customerId = customerResult.customerId;
          }
        }
        
        // Prepare booking document
        const now = new Date();
        const bookingEnd = new Date(bookingDateTime.getTime() + (bookingData.duration || 60) * 60000);
        
        const newBooking = {
          business_id: businessId,
          customer_name: bookingData.customerName,
          customer_whatsapp: bookingData.customerWhatsapp || bookingData.customerPhone,
          date: bookingData.bookingDate,
          booking_date: bookingData.bookingDate,
          time: bookingData.time,
          event_start: bookingDateTime.toISOString(),
          event_end: bookingEnd.toISOString(),
          service_type: bookingData.serviceName,
          service_name: bookingData.serviceName,
          service_price: parseFloat(bookingData.servicePrice || 0),
          service_duration: parseInt(bookingData.duration || 60),
          duration_minutes: parseInt(bookingData.duration || 60),
          status: 'confirmed',
          summary: `${bookingData.serviceName} - ${bookingData.customerName}`,
          notes: bookingData.notes || '',
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
          created_by: 'dashboard_atomic',
          source: 'conflict_resolved',
          conflict_resolution: {
            lockId: lock.lockId,
            checkedAt: availability.checkedAt,
            method: 'atomic_transaction'
          }
        };
        
        // Create booking within transaction
        const bookingRef = doc(collection(db, 'businesses', businessId, 'customers', customerId, 'bookings'));
        transaction.set(bookingRef, newBooking);
        
        return {
          bookingId: bookingRef.id,
          customerId: customerId,
          booking: { ...newBooking, id: bookingRef.id }
        };
      });
      
      // Sync to calendar outside of transaction
      try {
        const { BookingService } = await import('./BookingService');
        await BookingService.syncBookingToCalendar(businessId, {
          ...result.booking,
          customerId: result.customerId
        }, 'create');
      } catch (calendarError) {
        console.warn('Calendar sync failed:', calendarError);
        // Don't fail the booking creation if calendar sync fails
      }
      
      return {
        success: true,
        ...result
      };
      
    } catch (error) {
      throw error;
    } finally {
      // Always release lock
      if (lock) {
        await this.releaseLock(lock);
      }
    }
  }

  /**
   * Capacity-aware atomic booking creation using Firestore transactions
   */
  static async createBookingWithLock(businessId, bookingData, options = {}) {
    const { db } = FirebaseService.getInstances();
    let lock = null;

    try {
      const bookingDate = bookingData.bookingDate || bookingData.booking_date || bookingData.date;
      const bookingTime = bookingData.time || bookingData.booking_time;
      const serviceName = bookingData.serviceName || bookingData.service_name || bookingData.service_type || null;
      const duration = parseInt(
        bookingData.duration || bookingData.duration_minutes || bookingData.durationMinutes || 60,
        10
      );

      if (!bookingDate || !bookingTime) {
        throw new Error('Booking date and time are required for atomic creation');
      }

      const bookingDateTime = new Date(`${bookingDate}T${bookingTime}`);
      if (isNaN(bookingDateTime.getTime())) {
        throw new Error('Invalid booking date/time for atomic creation');
      }

      // Step 1: Capacity pre-check before attempting to acquire a lock
      const availability = await this.checkTimeSlotAvailability(
        businessId,
        bookingDateTime.toISOString(),
        serviceName,
        duration,
        options
      );

      if (!availability.isAvailable) {
        return {
          success: false,
          error: 'Time slot not available',
          availability,
          conflicts: availability.conflicts
        };
      }

      // Step 2: Acquire lock to minimise race conditions across concurrent requests
      lock = await this.acquireTimeSlotLock(businessId, bookingDateTime.toISOString(), duration);

      const { BookingService } = await import('./BookingService');
      const normalizedBookingData = { ...bookingData };

      if (!normalizedBookingData.customerId) {
        const lookupPhone = normalizedBookingData.customerPhone || normalizedBookingData.customer_phone;
        if (lookupPhone) {
          const existingCustomer = await BookingService.findCustomerByPhone(businessId, lookupPhone);
          if (existingCustomer) {
            normalizedBookingData.customerId = existingCustomer.id;
          }
        }
      }

      // Step 3: Execute Firestore transaction for atomic customer + booking creation
      const transactionResult = await runTransaction(db, async (transaction) => {
        const finalAvailability = await this.checkTimeSlotAvailability(
          businessId,
          bookingDateTime.toISOString(),
          serviceName,
          duration,
          options
        );

        if (!finalAvailability.isAvailable) {
          throw new Error('Time slot became unavailable during transaction');
        }

        const creationResult = await BookingService.createBookingAtomic(
          businessId,
          normalizedBookingData,
          transaction
        );

        if (!creationResult.success) {
          throw new Error(creationResult.error || 'Atomic booking creation failed');
        }

        return {
          ...creationResult,
          availability: finalAvailability
        };
      });

      return {
        success: true,
        ...transactionResult,
        lockInfo: lock
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'createBookingWithLock',
        businessId,
        bookingId: bookingData?.id,
        error: error.message
      });

      return {
        success: false,
        error: error.message
      };

    } finally {
      if (lock) {
        await this.releaseLock(lock);
      }
    }
  }

  // ==========================================
  // BOOKING VALIDATION METHODS
  // ==========================================

  /**
   * Comprehensive booking request validation
   */
  static async validateBookingRequest(businessId, bookingData) {
    const errors = [];
    const warnings = [];
    
    try {
      // Required field validation
      if (!bookingData.customerName) errors.push('Customer name is required');
      if (!bookingData.customerPhone) errors.push('Customer phone is required');
      if (!bookingData.serviceName) errors.push('Service name is required');
      if (!bookingData.bookingDate) errors.push('Booking date is required');
      if (!bookingData.time) errors.push('Booking time is required');
      
      // Date/time validation
      if (bookingData.bookingDate && bookingData.time) {
        const bookingDateTime = new Date(`${bookingData.bookingDate}T${bookingData.time}`);
        const now = new Date();
        
        if (bookingDateTime <= now) {
          errors.push('Booking time must be in the future');
        }
        
        // Check if booking is too far in the future (optional)
        const maxAdvanceBookingDays = 365; // 1 year
        const maxDate = new Date(now.getTime() + (maxAdvanceBookingDays * 24 * 60 * 60 * 1000));
        if (bookingDateTime > maxDate) {
          warnings.push(`Booking is more than ${maxAdvanceBookingDays} days in advance`);
        }
      }
      
      // Business hours validation (if strict validation enabled)
      if (this.STRICT_TIME_VALIDATION) {
        const businessHoursValid = await this.validateBusinessHours(businessId, bookingData);
        if (!businessHoursValid.isValid) {
          errors.push(...businessHoursValid.errors);
        }
      }
      
      // Duration validation
      const duration = parseInt(bookingData.duration || 60);
      if (duration < 15) warnings.push('Booking duration is very short (less than 15 minutes)');
      if (duration > 480) warnings.push('Booking duration is very long (more than 8 hours)');
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        validatedAt: new Date().toISOString()
      };
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'validateBookingRequest',
        businessId
      });
      
      return {
        isValid: false,
        errors: ['Validation failed due to system error'],
        warnings: [],
        validatedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Validate business hours (placeholder for future enhancement)
   */
  static async validateBusinessHours(businessId, bookingData) {
    // This would integrate with your business settings to check operating hours
    // For now, return valid to maintain non-breaking behavior
    return {
      isValid: true,
      errors: [],
      checked: 'placeholder'
    };
  }

  // ==========================================
  // ALTERNATIVE TIME SLOT SUGGESTIONS
  // ==========================================

  /**
   * Suggest alternative time slots when conflicts occur
   */
  static async suggestAlternativeTimeSlots(businessId, date, duration = 60, count = 5) {
    try {
      const alternatives = [];
      const baseDate = new Date(date);
      
      // Check time slots throughout the day
      for (let hour = 9; hour <= 17; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          if (alternatives.length >= count) break;
          
          const testDateTime = new Date(baseDate);
          testDateTime.setHours(hour, minute, 0, 0);
          
          const availability = await this.checkTimeSlotAvailability(
            businessId,
            testDateTime.toISOString(),
            duration
          );
          
          if (availability.isAvailable) {
            alternatives.push({
              dateTime: testDateTime.toISOString(),
              date: testDateTime.toISOString().split('T')[0],
              time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
              duration
            });
          }
        }
        if (alternatives.length >= count) break;
      }
      
      return alternatives;
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'suggestAlternativeTimeSlots',
        businessId,
        date
      });
      
      return [];
    }
  }

  // ==========================================
  // CONFLICT NOTIFICATION METHODS
  // ==========================================

  /**
   * Send conflict resolution notifications
   */
  static async sendConflictResolutionNotification(businessId, conflictData) {
    try {
      if (conflictData.type === 'conflict_detected') {
        await NotificationService.sendConflictAlert(businessId, {
          conflictCount: conflictData.conflicts?.length || 0,
          requestedTime: conflictData.requestedTime,
          customerName: conflictData.customerName
        });
      } else if (conflictData.type === 'conflict_resolved') {
        await NotificationService.sendBookingConfirmation(businessId, conflictData.booking);
      }
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'sendConflictResolutionNotification',
        businessId,
        conflictType: conflictData.type
      });
    }
  }

  // ==========================================
  // CLEANUP METHODS
  // ==========================================

  /**
   * Clean up expired locks (maintenance method)
   */
  static async cleanupExpiredLocks() {
    try {
      const { db } = FirebaseService.getInstances();
      
      const now = new Date().toISOString();
      const expiredLocksQuery = query(
        collection(db, 'booking_locks'),
        where('status', '==', 'active'),
        where('expiresAt', '<', now),
        limit(100)
      );
      
      const expiredSnap = await getDocs(expiredLocksQuery);
      
      for (const lockDoc of expiredSnap.docs) {
        await runTransaction(db, async (transaction) => {
          transaction.update(lockDoc.ref, {
            status: 'expired',
            expiredAt: serverTimestamp()
          });
        });
      }
      
      ErrorTrackingService.addBreadcrumb(
        'Expired locks cleanup completed',
        'conflict_resolution',
        'info',
        { cleanedCount: expiredSnap.docs.length }
      );
      
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ConflictResolutionService',
        method: 'cleanupExpiredLocks'
      });
    }
  }
}
